import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Plus, Edit2, Trash2, Save, X, GripVertical } from 'lucide-react';
import { toast } from 'sonner';
import type { AttributeGroup, Attribute } from '@/pages/AttributeManagement';

interface AttributeManagerProps {
  groups: AttributeGroup[];
  attributes: Attribute[];
  onAttributesChange: (attributes: Attribute[]) => void;
  activeGroupId: string;
}

const AttributeManager = ({ 
  groups, 
  attributes, 
  onAttributesChange, 
  activeGroupId 
}: AttributeManagerProps) => {
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [editingAttribute, setEditingAttribute] = useState<Attribute | null>(null);
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);
  const [draggedItem, setDraggedItem] = useState<string | null>(null);

  const [formData, setFormData] = useState({
    name: '',
    displayName: '',
    type: 'Text' as Attribute['type'],
    uiElementType: 'Textbox' as Attribute['uiElementType'],
    values: [''] as string[]
  });

  const activeGroupAttributes = attributes
    .filter(attr => attr.groupId === activeGroupId)
    .sort((a, b) => a.order - b.order);

  const attributeTypes: Attribute['type'][] = ['Integer', 'Number', 'Text', 'Float', 'Boolean', 'String Array'];
  const uiElementTypes: Attribute['uiElementType'][] = ['Checkbox', 'Dropdown (Single)', 'Dropdown (Multi)', 'Textbox', 'Textarea'];

  const resetForm = () => {
    setFormData({
      name: '',
      displayName: '',
      type: 'Text',
      uiElementType: 'Textbox',
      values: ['']
    });
  };

  const handleCreate = () => {
    if (!formData.name.trim() || !formData.displayName.trim()) {
      toast.error('Please fill in all required fields');
      return;
    }

    if (attributes.some(attr => attr.name.toLowerCase() === formData.name.toLowerCase())) {
      toast.error('Attribute name already exists');
      return;
    }

    const newAttribute: Attribute = {
      id: Date.now().toString(),
      name: formData.name.trim(),
      displayName: formData.displayName.trim(),
      type: formData.type,
      uiElementType: formData.uiElementType,
      groupId: activeGroupId,
      order: activeGroupAttributes.length + 1,
      values: (formData.uiElementType === 'Dropdown (Single)' || formData.uiElementType === 'Dropdown (Multi)') 
        ? formData.values.filter(v => v.trim()) 
        : undefined
    };

    onAttributesChange([...attributes, newAttribute]);
    setShowCreateDialog(false);
    resetForm();
    
    toast.success(`Attribute "${newAttribute.displayName}" created successfully`);
  };

  const handleEdit = (attribute: Attribute) => {
    setEditingAttribute(attribute);
    setFormData({
      name: attribute.name,
      displayName: attribute.displayName,
      type: attribute.type,
      uiElementType: attribute.uiElementType,
      values: attribute.values || ['']
    });
    setShowCreateDialog(true);
  };

  const handleUpdate = () => {
    if (!editingAttribute) return;

    if (!formData.name.trim() || !formData.displayName.trim()) {
      toast.error('Please fill in all required fields');
      return;
    }

    if (attributes.some(attr => 
      attr.id !== editingAttribute.id && 
      attr.name.toLowerCase() === formData.name.toLowerCase()
    )) {
      toast.error('Attribute name already exists');
      return;
    }

    const updatedAttributes = attributes.map(attr =>
      attr.id === editingAttribute.id
        ? {
            ...attr,
            name: formData.name.trim(),
            displayName: formData.displayName.trim(),
            type: formData.type,
            uiElementType: formData.uiElementType,
            values: (formData.uiElementType === 'Dropdown (Single)' || formData.uiElementType === 'Dropdown (Multi)') 
              ? formData.values.filter(v => v.trim()) 
              : undefined
          }
        : attr
    );

    onAttributesChange(updatedAttributes);
    setShowCreateDialog(false);
    setEditingAttribute(null);
    resetForm();
    
    toast.success('Attribute updated successfully');
  };

  const handleDelete = () => {
    if (!deleteConfirmId) return;

    const updatedAttributes = attributes.filter(attr => attr.id !== deleteConfirmId);
    onAttributesChange(updatedAttributes);
    setDeleteConfirmId(null);
    
    toast.success('Attribute deleted successfully');
  };

  const handleDragStart = (e: React.DragEvent, attributeId: string) => {
    setDraggedItem(attributeId);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', attributeId);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = (e: React.DragEvent, targetAttributeId: string) => {
    e.preventDefault();
    
    if (!draggedItem || draggedItem === targetAttributeId) {
      setDraggedItem(null);
      return;
    }

    const draggedAttr = attributes.find(attr => attr.id === draggedItem);
    const targetAttr = attributes.find(attr => attr.id === targetAttributeId);

    if (!draggedAttr || !targetAttr || draggedAttr.groupId !== targetAttr.groupId) {
      setDraggedItem(null);
      return;
    }

    const groupAttrs = activeGroupAttributes;
    const draggedIndex = groupAttrs.findIndex(attr => attr.id === draggedItem);
    const targetIndex = groupAttrs.findIndex(attr => attr.id === targetAttributeId);

    const reorderedAttrs = [...groupAttrs];
    const [movedItem] = reorderedAttrs.splice(draggedIndex, 1);
    reorderedAttrs.splice(targetIndex, 0, movedItem);

    // Update order values
    const updatedAttributes = attributes.map(attr => {
      if (attr.groupId !== activeGroupId) return attr;
      
      const newIndex = reorderedAttrs.findIndex(reorderedAttr => reorderedAttr.id === attr.id);
      return { ...attr, order: newIndex + 1 };
    });

    onAttributesChange(updatedAttributes);
    setDraggedItem(null);
    
    toast.success('Attribute order updated');
  };

  const handleCloseDialog = () => {
    setShowCreateDialog(false);
    setEditingAttribute(null);
    resetForm();
  };

  if (!activeGroupId) {
    return (
      <div className="text-center py-8">
        <p className="text-muted-foreground">Please select a group to manage attributes</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Top Create Button */}
      <div className="flex justify-end">
        <Button onClick={() => setShowCreateDialog(true)} className="gap-2">
          <Plus size={16} />
          Create Attribute
        </Button>
      </div>

      {/* Attributes Table */}
      <div className="border rounded-lg">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-10"></TableHead>
              <TableHead>Name</TableHead>
              <TableHead>Display Name</TableHead>
              <TableHead>Type</TableHead>
              <TableHead>UI Element</TableHead>
              <TableHead>Values</TableHead>
              <TableHead className="w-24">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {activeGroupAttributes.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                  No attributes found. Create your first attribute to get started.
                </TableCell>
              </TableRow>
            ) : (
              activeGroupAttributes.map((attribute) => (
                <TableRow
                  key={attribute.id}
                  draggable
                  onDragStart={(e) => handleDragStart(e, attribute.id)}
                  onDragOver={handleDragOver}
                  onDrop={(e) => handleDrop(e, attribute.id)}
                  className={`cursor-move ${draggedItem === attribute.id ? 'opacity-50' : ''}`}
                >
                  <TableCell>
                    <GripVertical size={16} className="text-muted-foreground" />
                  </TableCell>
                  <TableCell className="font-medium">{attribute.name}</TableCell>
                  <TableCell>{attribute.displayName}</TableCell>
                  <TableCell>{attribute.type}</TableCell>
                  <TableCell>{attribute.uiElementType}</TableCell>
                  <TableCell>
                    {attribute.values && attribute.values.length > 0 
                      ? attribute.values.join(', ') 
                      : '-'
                    }
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleEdit(attribute)}
                      >
                        <Edit2 size={14} />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => setDeleteConfirmId(attribute.id)}
                      >
                        <Trash2 size={14} />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {/* Create/Edit Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={handleCloseDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              {editingAttribute ? 'Edit Attribute' : 'Create New Attribute'}
            </DialogTitle>
            <DialogDescription>
              {editingAttribute ? 'Update the attribute details below.' : 'Fill in the attribute details below.'}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name">Name *</Label>
              <Input
                id="name"
                placeholder="e.g., product_name"
                value={formData.name}
                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="displayName">Display Name *</Label>
              <Input
                id="displayName"
                placeholder="e.g., Product Name"
                value={formData.displayName}
                onChange={(e) => setFormData(prev => ({ ...prev, displayName: e.target.value }))}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="type">Type</Label>
              <Select
                value={formData.type}
                onValueChange={(value) => setFormData(prev => ({ ...prev, type: value as Attribute['type'] }))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {attributeTypes.map(type => (
                    <SelectItem key={type} value={type}>
                      {type}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="uiElement">UI Element Type</Label>
              <Select
                value={formData.uiElementType}
                onValueChange={(value) => setFormData(prev => ({ ...prev, uiElementType: value as Attribute['uiElementType'] }))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {uiElementTypes.map(type => (
                    <SelectItem key={type} value={type}>
                      {type}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Values for Dropdown types */}
            {(formData.uiElementType === 'Dropdown (Single)' || formData.uiElementType === 'Dropdown (Multi)') && (
              <div className="space-y-2">
                <Label>Values</Label>
                <div className="space-y-2">
                  {formData.values.map((value, index) => (
                    <div key={index} className="flex items-center gap-2">
                      <Input
                        placeholder="Enter value..."
                        value={value}
                        onChange={(e) => {
                          const newValues = [...formData.values];
                          newValues[index] = e.target.value;
                          setFormData(prev => ({ ...prev, values: newValues }));
                        }}
                      />
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          const newValues = [...formData.values, ''];
                          setFormData(prev => ({ ...prev, values: newValues }));
                        }}
                      >
                        <Plus size={16} />
                      </Button>
                      {formData.values.length > 1 && (
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          onClick={() => {
                            const newValues = formData.values.filter((_, i) => i !== index);
                            setFormData(prev => ({ ...prev, values: newValues }));
                          }}
                        >
                          <X size={16} />
                        </Button>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={handleCloseDialog}>
              Cancel
            </Button>
            <Button onClick={editingAttribute ? handleUpdate : handleCreate}>
              {editingAttribute ? 'Update' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={!!deleteConfirmId} onOpenChange={() => setDeleteConfirmId(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirm Delete</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete this attribute? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteConfirmId(null)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDelete}>
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bottom Create Button */}
      {activeGroupAttributes.length > 10 && <div className="flex justify-end pt-4">
        <Button onClick={() => setShowCreateDialog(true)} className="gap-2">
          <Plus size={16} />
          Create Attribute
        </Button>
      </div>}
    </div>
  );
};

export default AttributeManager;